import React, { useState, useRef } from "react";
import axiosInstance from "../../services/FetchNodeServices";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

const CHUNK_SIZE = 50; // 50 images per request

const ImageUploader = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selected = [...e.target.files];
    setFiles(selected);
    setUploadResult(null);
    if (selected.length > 0) {
      toast.success(
        `✅ ${selected.length} images selected and ready to upload.`,
        { autoClose: 2000 }
      );
    }
  };

  const uploadInChunks = async (allFiles) => {
    const totalChunks = Math.ceil(allFiles.length / CHUNK_SIZE);
    let totalUploaded = 0;
    let successChunks = 0;

    for (let i = 0; i < allFiles.length; i += CHUNK_SIZE) {
      const chunk = allFiles.slice(i, i + CHUNK_SIZE);
      const currentChunk = i / CHUNK_SIZE + 1;
      const percent = Math.round((currentChunk / totalChunks) * 100);

      Swal.update({
        html: `
          <div style="text-align:left;">
            <p style="font-size:15px; color:#555; margin-bottom:10px;">
              Uploading chunk <strong>${currentChunk}</strong> of <strong>${totalChunks}</strong>
            </p>
            <div class="progress" style="height:20px; border-radius:10px; margin-bottom:10px;">
              <div class="progress-bar progress-bar-striped progress-bar-animated bg-success"
                role="progressbar" style="width:${percent}%; font-size:12px;">
                ${percent}%
              </div>
            </div>
            <p style="font-size:12px; color:#888;">
              🖼️ Images uploaded: <strong>${Math.min(i + CHUNK_SIZE, allFiles.length)}</strong> / ${allFiles.length}
            </p>
            <p style="font-size:12px; color:#888;">
              ✅ Completed chunks: <strong>${successChunks}</strong> / ${totalChunks}
            </p>
            <p style="font-size:12px; color:#888;">
              🔄 Saving with <strong>original filename</strong> (ISBN13.jpg)
            </p>
          </div>
        `,
      });

      const formData = new FormData();
      chunk.forEach((file) => formData.append("images", file));

      try {
        const res = await axiosInstance.post(
          "/api/v1/product/upload-multiple-products",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        totalUploaded += res.data.uploaded || chunk.length;
        successChunks++;
      } catch (error) {
        Swal.close();
        await Swal.fire({
          icon: "error",
          title: "❌ Upload Failed!",
          html: `
            <div style="text-align:left; background:#f8d7da; padding:15px; border-radius:8px;">
              <p style="font-size:14px; color:#721c24; font-weight:bold; margin-bottom:10px;">
                Failed at chunk ${currentChunk} of ${totalChunks}
              </p>
              <hr/>
              <ul style="font-size:13px; color:#555; padding-left:20px;">
                <li>✅ <strong>${successChunks} chunks</strong> completed</li>
                <li>✅ <strong>${totalUploaded}</strong> images uploaded</li>
                <li>❌ <strong>Chunk ${currentChunk}</strong> failed</li>
                <li>⏳ <strong>${totalChunks - currentChunk}</strong> chunks remaining</li>
              </ul>
              <p style="font-size:13px; color:red; margin-top:10px; font-weight:bold;">
                ⚠️ Already uploaded images are saved. Re-upload only failed/remaining images.
              </p>
            </div>
          `,
          confirmButtonText: "OK",
          confirmButtonColor: "#d33",
        });
        throw error;
      }
    }
    return totalUploaded;
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error("Please select images first.");
      return;
    }

    Swal.fire({
      title: "<strong>🖼️ Uploading Images</strong>",
      html: `
        <div style="text-align:left;">
          <p style="font-size:15px; color:#555; margin-bottom:10px;">
            Starting upload of <strong>${files.length}</strong> images...
          </p>
          <div class="progress" style="height:20px; border-radius:10px;">
            <div class="progress-bar progress-bar-striped progress-bar-animated bg-success"
              style="width:5%;">5%</div>
          </div>
          <p style="font-size:12px; color:#888; margin-top:10px;">
            🔄 Saving with <strong>original filename</strong> (ISBN13.jpg)
          </p>
        </div>
      `,
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      setUploading(true);
      setUploadResult(null);

      const totalUploaded = await uploadInChunks(files);

      Swal.close();
      Swal.fire({
        icon: "success",
        title: "✅ Images Uploaded!",
        html: `
          <div style="text-align:left; background:#f8f9fa; padding:15px; border-radius:8px;">
            <p style="font-size:15px; color:#444; margin-bottom:10px;">
              <strong>${totalUploaded}</strong> images uploaded successfully!
            </p>
            <hr/>
            <p style="font-size:13px; color:#666; font-weight:bold; margin-bottom:8px;">
              📋 What happened:
            </p>
            <ul style="font-size:13px; color:#555; padding-left:20px; margin-bottom:0;">
              <li>✅ Images saved with <strong>original filename</strong></li>
              <li>✅ Saved to <strong>/public/images/</strong> folder</li>
              <li>🔄 Existing image → <strong>REPLACED</strong> with new one</li>
              <li>📦 Uploaded in chunks of <strong>${CHUNK_SIZE} images</strong></li>
            </ul>
          </div>
        `,
        confirmButtonText: "Done ✅",
        confirmButtonColor: "#28a745",
      });

      setUploadResult({
        success: true,
        uploaded: totalUploaded,
        total: files.length,
      });

      setFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";

    } catch (error) {
      setUploadResult({ success: false });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="container mt-5">
      <div className="card shadow">
        <div className="card-body">
          <div className="p-4 mb-2 text-start">
            <h4 className="card-title mb-3">Upload Multiple Images</h4>

            {/* ℹ️ How it works */}
            <div style={{
              background: "#e8f4fd",
              border: "1px solid #3498db",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "12px"
            }}>
              <p style={{ fontWeight: "bold", color: "#2980b9", marginBottom: "8px" }}>
                ℹ️ How this upload works:
              </p>
              <ul style={{ fontSize: "13px", color: "#555", paddingLeft: "20px", marginBottom: "0" }}>
                <li>🖼️ <strong>Saves with original filename</strong> (e.g. 9780023487507.jpg)</li>
                <li>🔄 <strong>Existing image?</strong> → <span style={{ color: "blue", fontWeight: "bold" }}>REPLACED</span></li>
                <li>✅ <strong>New image?</strong> → <span style={{ color: "green", fontWeight: "bold" }}>CREATED</span> in /public/images/</li>
                <li>📦 <strong>Uploads in chunks</strong> of {CHUNK_SIZE} images per request</li>
                <li>⚠️ Max file size: <strong>5MB per image</strong></li>
                <li>✅ Accepted: <strong>JPG, PNG, WEBP, GIF, JFIF</strong></li>
              </ul>
            </div>

            {/* ⚠️ Naming note */}
            <div style={{
              background: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "8px",
              padding: "12px",
            }}>
              <p style={{ fontWeight: "bold", color: "#856404", marginBottom: "5px" }}>
                ⚠️ Important — Image naming:
              </p>
              <ul style={{ fontSize: "13px", color: "#555", paddingLeft: "20px", marginBottom: "0" }}>
                <li>Name images with <strong>ISBN13</strong> → <code>9780023487507.jpg</code></li>
                <li>This links the image to the correct product automatically</li>
              </ul>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileChange}
            className="form-control mb-3"
          />

          {/* File count */}
          {files.length > 0 && (
            <div className="alert alert-info">
              🖼️ <strong>{files.length}</strong> images selected and ready to upload
            </div>
          )}

          {/* Success Result */}
          {uploadResult?.success && (
            <div className="alert alert-success">
              🎉 <strong>Upload Successful!</strong><br />
              ✅ <strong>{uploadResult.uploaded}</strong> out of{" "}
              <strong>{uploadResult.total}</strong> images uploaded successfully.
            </div>
          )}

          {/* Failed Result */}
          {uploadResult?.success === false && (
            <div className="alert alert-danger">
              ❌ <strong>Upload Failed!</strong> Please re-upload remaining images.
            </div>
          )}

          <button
            onClick={handleUpload}
            disabled={uploading || files.length === 0}
            className="btn btn-primary"
          >
            {uploading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" />
                Uploading...
              </>
            ) : `Upload ${files.length > 0 ? files.length + " Images" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageUploader;
